import {
  Image as ReactPdfImage,
  Polygon as ReactPdfPolygon,
  Svg as ReactPdfSvg,
  Text as ReactPdfText,
  View,
} from '@react-pdf/renderer';
import React from 'react';

import { SVGPoiData } from '../../../../../../utils/three/vectorize-geometry';
import { isDefined } from '../../../../../../utils/variables';
import { pdfPageDimensions } from '../helpers/types';

type PoiThumbnailProps = {
  poi: SVGPoiData;
  preview: boolean;
  svgWidth?: number;
  svgHeight?: number;
};

export const PoiThumbnail: React.FC<PoiThumbnailProps> = ({
  poi,
  preview,
  svgWidth,
  svgHeight,
}) => {
  // shared geometry
  const thumbnailSize = 40; // Total frame size including border
  const triangleHeight = 5;
  const triangleBase = 12;
  const borderW = 1;
  const triangleOverlap = borderW;

  const padding = pdfPageDimensions.a3.landscape72Dpi.defaultPadding;
  const containerWidth = pdfPageDimensions.a3.landscape72Dpi.width - padding * 2;
  const containerHeight = pdfPageDimensions.a3.landscape72Dpi.height - padding * 2;

  const hasDims = !!(svgWidth && svgHeight);
  const scale = hasDims ? Math.min(containerWidth / svgWidth, containerHeight / svgHeight) : 1;
  const letterboxX = hasDims ? (containerWidth - svgWidth * scale) / 2 : 0;
  const letterboxY = hasDims ? (containerHeight - svgHeight * scale) / 2 : 0;

  const X = poi.x * scale + letterboxX;
  const Y = poi.y * scale + letterboxY;

  const isCluster = isDefined(poi.clusterSize);
  const is360Poi = poi.is360Poi === true;
  const skymapYellow = '#ffdd33';

  // anchors
  const left = Math.round(X - thumbnailSize / 2);
  const top = Math.round(
    isCluster ? Y - thumbnailSize : Y - (thumbnailSize + triangleHeight - triangleOverlap),
  );

  // sizes/radii
  const innerSide = thumbnailSize - borderW * 2;

  // non-cluster (rounded rect or circle for 360 POIs)
  const outerRadiusRect = is360Poi ? thumbnailSize / 2 : thumbnailSize * 0.1; // 10% corners or fully round for 360 POIs
  const innerRadiusRect = Math.max(0, outerRadiusRect - borderW);

  // cluster (circle)
  const outerRadiusCircle = thumbnailSize / 2;
  const innerRadiusCircle = outerRadiusCircle - borderW;

  if (isCluster) {
    const ClusterRing = preview ? 'div' : View;
    const ClusterImg = preview ? 'img' : ReactPdfImage;
    const ClusterText = preview ? 'div' : ReactPdfText;

    // Badge dimensions - dynamic sizing based on cluster size
    const clusterSizeStr = poi.clusterSize?.toString() || '';
    const minBadgeSize = 16;
    const padding = 6; // horizontal padding around text
    const badgeSize = Math.max(minBadgeSize, clusterSizeStr.length * 8 + padding);
    const badgeRadius = badgeSize / 2;
    const badgeBorderWidth = 1;

    return (
      <ClusterRing
        style={{
          position: 'absolute',
          left,
          top,
          width: thumbnailSize,
          height: thumbnailSize,
          alignItems: 'center',
          justifyContent: 'center',
          display: preview ? 'flex' : undefined,
        }}
      >
        <ClusterRing
          style={{
            width: thumbnailSize,
            height: thumbnailSize,
            borderWidth: borderW,
            borderColor: skymapYellow,
            borderStyle: 'solid',
            borderRadius: outerRadiusCircle,
            padding: borderW,
            boxSizing: preview ? 'border-box' : undefined,
            backgroundColor: skymapYellow, // fills any sub-pixel seam
            alignItems: 'center',
            justifyContent: 'center',
            display: preview ? 'flex' : undefined,
          }}
        >
          <ClusterImg
            alt={preview ? 'Cluster thumbnail' : undefined}
            src={poi.thumbnailUrl}
            style={
              {
                display: preview ? 'block' : undefined,
                width: innerSide,
                height: innerSide,
                borderRadius: innerRadiusCircle, // ← key: inner radius = outer - borderW
                objectFit: 'cover',
              } as any
            }
          />
        </ClusterRing>

        {/* Cluster size badge */}
        <ClusterRing
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: badgeSize,
            height: badgeSize,
            borderWidth: badgeBorderWidth,
            borderColor: skymapYellow,
            borderStyle: 'solid',
            borderRadius: badgeRadius,
            backgroundColor: skymapYellow,
            alignItems: 'center',
            justifyContent: 'center',
            display: preview ? 'flex' : undefined,
          }}
        >
          <ClusterText
            style={{
              fontSize: 14,
              fontWeight: 'bold',
              textAlign: 'center',
              lineHeight: 1,
              color: '#000000',
              ...(preview ? {} : { fontFamily: 'Helvetica' }),
            }}
          >
            {poi.clusterSize}
          </ClusterText>
        </ClusterRing>
      </ClusterRing>
    );
  }

  const RectRing = preview ? 'div' : View;
  const RectImg = preview ? 'img' : ReactPdfImage;
  const RectText = preview ? 'div' : ReactPdfText;

  // Badge dimensions for 360 POI badge
  const badge360Text = '360°';

  return (
    <RectRing
      style={{
        position: 'absolute',
        left,
        top,
        width: thumbnailSize,
        height: thumbnailSize + triangleHeight,
        textAlign: preview ? 'center' : undefined,
        lineHeight: preview ? 0 : undefined,
        alignItems: preview ? undefined : 'center',
      }}
    >
      <RectRing
        style={{
          width: thumbnailSize,
          height: thumbnailSize,
          borderWidth: borderW,
          borderColor: skymapYellow,
          borderStyle: 'solid',
          borderRadius: outerRadiusRect,
          padding: borderW,
          boxSizing: preview ? 'border-box' : undefined,
          backgroundColor: skymapYellow, // ← fills micro gaps
          alignItems: 'center',
          justifyContent: 'center',
          display: preview ? 'flex' : undefined,
        }}
      >
        <RectImg
          alt={preview ? 'POI thumbnail' : undefined}
          src={poi.thumbnailUrl}
          style={
            {
              display: preview ? 'block' : undefined,
              width: innerSide,
              height: innerSide,
              borderRadius: innerRadiusRect, // ← outer - borderW
              objectFit: 'cover',
            } as any
          }
        />
      </RectRing>

      {/* 360 POI badge */}
      {is360Poi && (
        <RectRing
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '3px',
            borderRadius: '3px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)', // black 80% opacity
            alignItems: 'center',
            justifyContent: 'center',
            display: preview ? 'flex' : undefined,
          }}
        >
          <RectText
            style={{
              fontSize: 8,
              fontWeight: 'bold',
              textAlign: 'center',
              lineHeight: 1,
              color: '#FFFFFF', // white text for black background
              ...(preview ? {} : { fontFamily: 'Helvetica' }),
            }}
          >
            {badge360Text}
          </RectText>
        </RectRing>
      )}

      {/* pointer (unchanged) */}
      {preview ? (
        <div
          style={{
            width: triangleBase,
            height: triangleHeight,
            marginTop: -triangleOverlap, // overlap to remove joint line
            marginInline: 'auto',
            display: 'block',
          }}
        >
          <svg
            height={triangleHeight}
            style={{ display: 'block' }}
            viewBox={`0 0 ${triangleBase} ${triangleHeight}`}
            width={triangleBase}
          >
            <polygon
              fill="#ffdd33"
              points={`0,0 ${triangleBase},0 ${triangleBase / 2},${triangleHeight}`}
            />
          </svg>
        </div>
      ) : (
        <View
          style={{
            width: triangleBase,
            height: triangleHeight,
            marginTop: -triangleOverlap,
            alignItems: 'center',
          }}
        >
          <ReactPdfSvg
            height={triangleHeight}
            viewBox={`0 0 ${triangleBase} ${triangleHeight}`}
            width={triangleBase}
          >
            <ReactPdfPolygon
              fill="#ffdd33"
              points={`0,0 ${triangleBase},0 ${triangleBase / 2},${triangleHeight}`}
            />
          </ReactPdfSvg>
        </View>
      )}
    </RectRing>
  );
};
